import { httpsCallableFromURL } from "firebase/functions";
import {
  auth,
  firebaseProjectId,
  functions,
  functionsRegion,
  usingFunctionsEmulator,
} from "../app/firebase.config";
import { httpsCallable } from "./firebaseCallable";

const callable = (name) => httpsCallable(functions, name);
const callableWithTimeout = (name, timeoutMs) =>
  httpsCallable(functions, name, { timeout: timeoutMs });
const callableFromProductionUrl = (name) => {
  if (!firebaseProjectId) {
    return callable(name);
  }
  return httpsCallableFromURL(
    functions,
    `https://${functionsRegion}-${firebaseProjectId}.cloudfunctions.net/${name}`
  );
};

const mergeLlmContext = (existing = {}, defaults = {}) => {
  const existingTags = Array.isArray(existing.llmContextTags)
    ? existing.llmContextTags
    : [];
  const defaultTags = Array.isArray(defaults.llmContextTags)
    ? defaults.llmContextTags
    : [];
  return {
    ...defaults,
    ...existing,
    llmContextTags: Array.from(new Set([...defaultTags, ...existingTags])),
  };
};

export const fetchLatestPlan = async () => {
  const result = await callable("getLatestPlan")();
  return result.data;
};

export const fetchPlanTimeline = async (cursor) => {
  const result = await callable("getPlanTimeline")({ cursor });
  return result.data;
};

export const fetchPlanById = async (planId) => {
  const result = await callable("getPlanById")({ planId });
  return result.data;
};

export const createPlan = async (payload) => {
  const result = await callable("createPlan")(payload);
  return result.data;
};

export const updatePlan = async (planId, updates) => {
  const result = await callable("updatePlan")({ planId, updates });
  return result.data;
};

export const updatePlanSection = async (
  planId,
  sectionId,
  content,
  provenance,
  supportingDocuments
) => {
  const result = await callable("updatePlanSection")({
    planId,
    sectionId,
    content,
    provenance,
    supportingDocuments,
  });
  return result.data;
};

export const refinePlanSection = async (planId, sectionId, payload) => {
  const result = await callable("refinePlanSection")({
    planId,
    sectionId,
    ...payload,
  });
  return result.data;
};

export const rebalancePlan = async (planId, payload) => {
  const result = await callable("rebalancePlan")({ planId, ...payload });
  return result.data;
};

export const generateAssignments = async (planId, payload) => {
  const result = await callable("generateAssignments")({ planId, ...payload });
  return result.data;
};

export const fetchAssignments = async (planId) => {
  const result = await callable("getAssignments")({ planId });
  return result.data;
};

export const fetchCatalogAssignments = async (payload = {}) => {
  const result = await callable("getCatalogAssignments")(payload);
  return result.data;
};

export const fetchTeachLeagueFolders = async (payload = {}) => {
  // Avoid unauthenticated callable requests during initial auth bootstrap.
  if (!auth.currentUser) {
    return { folders: [] };
  }
  const result = await callable("getTeachLeagueFolders")(payload);
  return result.data;
};

export const updateAssignment = async (assignmentId, updates) => {
  const result = await callable("updateAssignment")({ assignmentId, updates });
  return result.data;
};

export const fetchAnalytics = async (planId) => {
  const result = await callable("getPlanAnalytics")({ planId });
  return result.data;
};

export const fetchCanvasAssignments = async () => {
  const result = await callable("getCanvasAssignments")();
  return result.data;
};

export const fetchCanvasSubmissions = async () => {
  const result = await callable("getCanvasSubmissions")();
  return result.data;
};

export const fetchCanvasCourses = async () => {
  const result = await callable("getCanvasCourses")();
  return result.data;
};

export const syncCanvasCourses = async (payload = {}) => {
  const result = await callable("syncCanvasCourses")(payload);
  return result.data;
};

export const saveCanvasIntegration = async (payload) => {
  const result = await callable("saveCanvasIntegration")(payload);
  return result.data;
};

export const fetchCanvasIntegrationStatus = async () => {
  const result = await callable("getCanvasIntegrationStatus")();
  return result.data;
};

export const disconnectCanvasIntegration = async () => {
  const result = await callable("disconnectCanvasIntegration")();
  return result.data;
};

export const syncCanvasAssignments = async (payload = {}) => {
  const result = await callable("syncCanvasAssignments")(payload);
  return result.data;
};

export const syncCanvasSubmissions = async (payload = {}) => {
  const result = await callableWithTimeout("syncCanvasSubmissions", 300000)(payload);
  return result.data;
};

export const gradeCanvasAssignment = async (payload) => {
  const result = await callable("gradeCanvasAssignment")({
    ...(payload || {}),
    llmContext: mergeLlmContext(payload?.llmContext, {
      llmSensitivity: "student_pii",
      llmContextTags: ["gradebook", "grading", "canvas"],
    }),
  });
  return result.data;
};

export const postCanvasGrades = async (payload) => {
  const result = await callable("postCanvasGrades")(payload);
  return result.data;
};

export const postCanvasAssignment = async (payload) => {
  const result = await callable("postCanvasAssignment")(payload);
  return result.data;
};

export const postCanvasAssignmentFromPlan = async (payload) => {
  const result = await callable("postCanvasAssignmentFromPlan")(payload);
  return result.data;
};

export const createAssignmentArtifacts = async (payload) => {
  const result = await callable("createAssignmentArtifacts")(payload);
  return result.data;
};

export const addAssignmentArtifact = async (payload) => {
  const result = await callable("addAssignmentArtifact")(payload);
  return result.data;
};

export const exportLessonPlanToDrive = async (payload) => {
  const result = await callable("exportLessonPlanToDrive")(payload);
  return result.data;
};

export const searchPerspectiveResources = async (payload = {}) => {
  // The local Functions emulator stubs outbound network calls, so the
  // Missing Voices search callable needs the deployed backend in dev.
  const invokeSearchPerspectiveResources = usingFunctionsEmulator
    ? callableFromProductionUrl("searchPerspectiveResources")
    : callable("searchPerspectiveResources");
  const result = await invokeSearchPerspectiveResources(payload);
  return result.data;
};

export const extractPerspectiveSourceUrl = async (payload = {}) => {
  // URL extraction also depends on outbound fetches, so local emulator runs
  // should use the deployed callable when available.
  const invokeExtractPerspectiveSourceUrl = usingFunctionsEmulator
    ? callableFromProductionUrl("extractPerspectiveSourceUrl")
    : callable("extractPerspectiveSourceUrl");
  const result = await invokeExtractPerspectiveSourceUrl(payload);
  return result.data;
};

export const publishStandardsDocumentToDrive = async (payload) => {
  const result = await callable("publishStandardsDocumentToDrive")(payload);
  return result.data;
};

export const analyzeStandardsDocuments = async (payload = {}) => {
  const result = await callable("analyzeStandardsDocuments")(payload);
  return result.data;
};

export const dashboardAssistant = async (payload = {}) => {
  const result = await callableWithTimeout("dashboardAssistant", 120000)({
    ...payload,
    llmContext: mergeLlmContext(payload?.llmContext, {
      llmSensitivity: "student_pii",
      llmContextTags: ["dashboard_assistant", "gradebook"],
    }),
  });
  return result.data;
};

export const recordInstructionalIntervention = async (payload = {}) => {
  const result = await callable("recordInstructionalIntervention")(payload);
  return result.data;
};

export const getInstructionalImpactSummary = async (payload = {}) => {
  const result = await callable("getInstructionalImpactSummary")(payload);
  return result.data;
};

export const getClassAnalytics = async (assignmentId) => {
  const result = await callable("getClassAnalytics")({ assignmentId });
  return result.data;
};

export const generateChecklist = async (payload) => {
  const result = await callable("generateChecklist")(payload);
  return result.data;
};

export const saveRubricDraft = async (payload) => {
  const result = await callable("saveRubricDraft")(payload);
  return result.data;
};
